import React, { useEffect, useMemo, useRef, useState } from "react";
import { DocRequestForm } from "../../pages/DocRequestForm/DocRequestForm";
import { useAuth } from "../../context/AuthContext";
import { BiUpload } from "react-icons/bi";
import { useToast } from "../../context/ToastContext";
import { uploadPendingAddDoc } from "../../services/api/AddDocApi";
import { useLocation, useNavigate } from "react-router-dom";
import { useBubbleSettings } from "../../context/BubbleSettingsContext";
import { updateUnseenNotification } from "../../services/api/NotificationsApi";
import { uploadFileWithProgress } from "../../utils/ftpUploadUtils";
import { ViewDocumentModal } from "./ViewDocumentModal";
import { IoGrid } from "react-icons/io5";
import { FaTableList, FaClipboardList } from "react-icons/fa6";
import { MdDescription } from "react-icons/md";
import "./style/RenderAdditionalDocs.scss";
import { FaCalendarAlt, FaFileAlt, FaInfoCircle } from "react-icons/fa";

export const RenderAdditionalDocs = ({
  additonaldocs,
  selectedCourtType,
  courtRef,
  refreshDocs,
}) => {
  // ==================== UTILITY FUNCTIONS ====================

  // Maximum file size in bytes (50MB)
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  // Allowed file types with their MIME types and extensions
  const ALLOWED_FILE_TYPES = {
    "application/pdf": { ext: ".pdf", name: "PDF" },
    "application/msword": { ext: ".doc", name: "Word Document" },
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
      ext: ".docx",
      name: "Word Document",
    },
    "text/plain": { ext: ".txt", name: "Text File" },
    "image/png": { ext: ".png", name: "PNG Image" },
    "image/jpeg": { ext: ".jpg,.jpeg", name: "JPEG Image" },
    "application/vnd.ms-excel": { ext: ".xls", name: "Excel Spreadsheet" },
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
      ext: ".xlsx",
      name: "Excel Spreadsheet",
    },
  };

  /**
   * Get the accept string for file input
   */
  const getAcceptString = () => {
    return ".pdf,.doc,.docx,.txt,.png,.jpeg,.jpg,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  };

  /**
   * Validate file type and size
   */
  const validateFileType = (file) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "image/png",
      "image/jpeg",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const allowedExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".txt",
      ".png",
      ".jpg",
      ".jpeg",
      ".xls",
      ".xlsx",
    ];

    if (!file) {
      return {
        isValid: false,
        errorType: "NO_FILE",
        error: "No file provided",
      };
    }

    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      return {
        isValid: false,
        errorType: "INVALID_TYPE",
        error: file.type || fileExtension,
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        errorType: "FILE_TOO_LARGE",
        error: file.size,
      };
    }

    return { isValid: true };
  };

  /**
   * Get error message based on error type
   */
  const getErrorMessage = (errorType, error) => {
    switch (errorType) {
      case "NO_FILE":
        return "Please select a file to upload";
      case "INVALID_TYPE":
        return `Invalid file type. Allowed formats: PDF, DOC, DOCX, TXT, PNG, JPEG, JPG, XLS, XLSX`;
      case "FILE_TOO_LARGE":
        const sizeMB = (error / (1024 * 1024)).toFixed(2);
        return `File size (${sizeMB}MB) exceeds the maximum limit of 50MB`;
      default:
        return "An error occurred while validating the file";
    }
  };

  // ==================== STATE MANAGEMENT ====================

  const { company } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [file, setDocFile] = useState(null);
  const [addedCourtRef, setAddedCourtRef] = useState(null);
  const { showToast } = useToast();
  const { role } = useAuth();
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentID, setDocumentID] = useState(null);
  const addedDocfileInputRef = useRef(null);

  const [uploadingMap, setUploadingMap] = useState({});
  const [uploadProgressMap, setUploadProgressMap] = useState({});
  const [fileMap, setFileMap] = useState({});
  const [documentUrlMap, setDocumentUrlMap] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // View mode state (table or grid)
  const [viewMode, setViewMode] = useState("table");

  // State for View Document Modal
  const [isViewDocModalOpen, setIsViewDocModalOpen] = useState(false);
  const [uploadModalDoc, setUploadModalDoc] = useState(null);
  const [uploadModalMode, setUploadModalMode] = useState("view");

  const navigate = useNavigate();
  const location = useLocation();

  const courtref = location.pathname.split("/")[4];

  // ==================== HELPER FUNCTIONS ====================

  // Helper function to truncate text by word and character count
  const truncateText = (text, wordLimit = 20, charLimit = 100) => {
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= wordLimit && text.length <= charLimit) return text;

    let truncated = words.slice(0, wordLimit).join(" ");
    if (truncated.length > charLimit) {
      truncated = truncated.slice(0, charLimit);
      truncated = truncated.replace(/\s+\S*$/, "");
    }
    return truncated + "...";
  };

  // Helper function to get char count
  const getCharCount = (text) => (text ? text.length : 0);

  // Helper function to get word count
  const getWordCount = (text) => {
    if (!text) return 0;
    return text.split(/\s+/).filter((word) => word.length > 0).length;
  };

  // Toggle description expansion
  const toggleDescription = (docId, e) => {
    e.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [docId]: !prev[docId],
    }));
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString("default", {
      month: "short",
    })} ${date.getFullYear().toString().slice(-2)}`;
  };

  // ==================== EFFECTS ====================

  useEffect(() => {
    if (additonaldocs.length > 0) {
      setAddedCourtRef(additonaldocs[additonaldocs.length - 1].courtRef);
    }
  }, [additonaldocs]);

  // ==================== HANDLERS ====================

  const handleFileChange = (e, docId) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file before proceeding
      const validation = validateFileType(selectedFile);

      if (!validation.isValid) {
        const errorMessage = getErrorMessage(
          validation.errorType,
          validation.error
        );
        showToast(errorMessage, "error");
        e.target.value = ""; // Clear the input
        return;
      }

      setFileMap((prev) => ({ ...prev, [docId]: selectedFile }));
      handleFileUpload(selectedFile, docId);
    }
  };

  const handleFileUpload = async (selectedFile, docId) => {
    if (!selectedFile) return;

    setUploadingMap((prev) => ({ ...prev, [docId]: true }));
    setUploadProgressMap((prev) => ({ ...prev, [docId]: 0 }));

    try {
      const filePath = company._id ? `/${company._id}/others` : `/unknown`;

      await uploadFileWithProgress(
        selectedFile,
        filePath,
        // Progress callback
        (progress) => {
          setUploadProgressMap((prev) => ({ ...prev, [docId]: progress }));
        },
        // Success callback
        (result) => {
          setDocumentUrlMap((prev) => ({ ...prev, [docId]: result.imageUrl }));
          setUploadingMap((prev) => ({ ...prev, [docId]: false }));
          showToast("File uploaded successfully!", "success");
        },
        // Error callback
        (error) => {
          console.error("File upload failed:", error);
          setUploadingMap((prev) => ({ ...prev, [docId]: false }));
          setUploadProgressMap((prev) => ({ ...prev, [docId]: 0 }));
          showToast(error.message || "File upload failed!", "error");
        }
      );
    } catch (error) {
      console.error("File upload failed:", error);
      setUploadingMap((prev) => ({ ...prev, [docId]: false }));
      setUploadProgressMap((prev) => ({ ...prev, [docId]: 0 }));
      showToast(error.message || "File upload failed!", "error");
    }
  };

  const handleUploadClick = () => {
    addedDocfileInputRef?.current?.click();
  };

  const handleSubmit = async (docId, url) => {
    if (url && docId) {
      const payload = { url };
      try {
        await uploadPendingAddDoc(payload, docId);
        showToast("Document URL submitted successfully!", "success");
        if (typeof refreshDocs === "function") {
          refreshDocs();
        }
      } catch (err) {
        console.error("Error submitting document URL:", err);
        showToast("Failed to submit document URL!", "error");
      }
    }
  };

  const { bubbleSettings, fetchAndUpdateBubbleSettings } = useBubbleSettings();

  const documentInfoMap = useMemo(() => {
    const map = {};

    const allCourts = [
      ...(bubbleSettings?.case?.highCourt || []),
      ...(bubbleSettings?.case?.districtCourt || []),
      ...(bubbleSettings?.case?.supremeCourt || []),
    ];

    allCourts.forEach((entry) => {
      (entry.document || []).forEach((doc) => {
        if (doc.source) {
          map[doc.source] = {
            action: doc.action,
            notificationId: doc.id,
          };
        }
      });
    });

    return map;
  }, [bubbleSettings]);

  const handleDocClick = async (docId) => {
    const info = documentInfoMap[docId];
    if (info?.notificationId) {
      try {
        await updateUnseenNotification(info.notificationId);
        await fetchAndUpdateBubbleSettings();
      } catch (err) {
        console.error("Error updating notification:", err);
      }
    }
    navigate(`/home/cases/casedetails/${courtref}/additonaldoc/${docId}`);
  };

  const handleActionClick = (e, doc, action) => {
    e.stopPropagation();
    setUploadModalDoc(doc);
    setUploadModalMode(action);
    setIsViewDocModalOpen(true);
  };

  // ==================== RENDER FUNCTIONS ====================

  // Table View Renderer
  const renderTableView = () => (
    <div className="table-responsive">
      <table className="documents-table">
        <thead>
          <tr>
            <th className="td-th-casedet">Name</th>
            <th className="td-th-casedet">Court Ref</th>
            <th className="td-th-casedet">Status</th>
            <th className="td-th-casedet">Description</th>
            <th className="td-th-casedet">Created At</th>
            <th style={{ textAlign: "center" }} className="td-th-casedet">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {additonaldocs.map((doc, index) => (
            <tr
              key={index}
              onClick={() => handleDocClick(doc._id)}
              title="Click to view details"
            >
              <td className="td-th-casedet">
                <strong>{doc.name}</strong>
              </td>
              <td className="td-th-casedet">{doc.courtRef}</td>
              <td className="td-th-casedet">
                {/* Show regular status badge */}
                <span
                  className={`status-badge ${
                    doc.status === "Requested"
                      ? "requested"
                      : doc.status === "Uploaded"
                      ? "uploaded"
                      : "pending"
                  }`}
                >
                  {doc.status === "Requested" ? "Pending" : doc.status}
                </span>
              </td>
              <td className="td-th-casedet">
                <div className="description-cell" title={doc.description}>
                  {truncateText(doc.description, 10, 50)}
                </div>
              </td>
              <td className="td-th-casedet">{formatDate(doc.createdAt)}</td>
              <td className="td-th-casedet actions-cell">
                {/* Show notification badge if there's a document action */}
                {documentInfoMap[doc._id]?.action && (
                  <span
                    className={`doc-status-badge ${
                      documentInfoMap[doc._id].action === "Document Uploaded"
                        ? "uploaded"
                        : "requested"
                    }`}
                  >
                    {documentInfoMap[doc._id].action === "Document Uploaded"
                      ? "Uploaded"
                      : "Requested"}
                  </span>
                )}
                <div className="table-actions">
                  {role === "customer" && (
                    <button
                      className="upload-btn"
                      onClick={(e) => handleActionClick(e, doc, "upload")}
                    >
                      {doc.status === "Requested" ? "Upload" : "Upload More"}
                    </button>
                  )}
                  {role !== "customer" && doc.status === "Uploaded" && (
                    <button
                      className="view-btn"
                      onClick={(e) => handleActionClick(e, doc, "view")}
                    >
                      View
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Grid View Renderer
  const renderGridView = () => (
    <div className="documents-grid-layout">
      {additonaldocs.map((doc, index) => (
        <div
          key={index}
          className="modern-document-card"
          onClick={() => handleDocClick(doc._id)}
        >
          {/* Status Badge */}
          {documentInfoMap[doc._id]?.action && (
            <span
              className={`doc-status-badge ${
                documentInfoMap[doc._id].action === "Document Uploaded"
                  ? "uploaded"
                  : "requested"
              }`}
            >
              {documentInfoMap[doc._id].action === "Document Uploaded"
                ? "Uploaded"
                : "Requested"}
            </span>
          )}

          {/* Header Strip */}
          <div className="card-header-strip">
            <div className="doc-name-badge">
              <FaFileAlt className="type-icon" />
              <span className="name-text">{doc.name}</span>
            </div>
          </div>

          {/* Content Body */}
          <div className="card-content-body">
            {/* First Row - Court Ref and Created At in same row */}
            <div className="detail-row-horizontal">
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaCalendarAlt className="row-icon" />
                  Created At
                </div>
                <div className="detail-value">{formatDate(doc.createdAt)}</div>
              </div>
              <div className="detail-row detail-row-half">
                <div className="detail-label">
                  <FaInfoCircle className="row-icon" />
                  Status
                </div>
                <div className="detail-value">
                  <span
                    className={`status-pill ${
                      doc.status === "Requested"
                        ? "requested"
                        : doc.status === "Uploaded"
                        ? "uploaded"
                        : "pending"
                    }`}
                  >
                    {doc.status === "Requested" ? "Pending" : doc.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Status Row */}
            <div className="detail-row">
              <div className="detail-label">
                <FaClipboardList className="row-icon" />
                Court Ref
              </div>
              <div className="detail-value">{doc.courtRef}</div>
            </div>

            {/* Description Row */}
            {doc.description && (
              <div className="detail-row">
                <div className="detail-label">
                  <MdDescription className="row-icon" />
                  Description
                </div>
                <div className="detail-value description-text">
                  {expandedDescriptions[doc._id]
                    ? doc.description
                    : getWordCount(doc.description) > 20 ||
                      getCharCount(doc.description) > 120
                    ? truncateText(doc.description, 20, 120)
                    : doc.description}
                  {(getWordCount(doc.description) > 20 ||
                    getCharCount(doc.description) > 120) && (
                    <button
                      className="read-more-btn"
                      onClick={(e) => toggleDescription(doc._id, e)}
                    >
                      {expandedDescriptions[doc._id]
                        ? "Show Less"
                        : "Read More"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="card-footer-actions">
            {role === "customer" && (
              <button
                className="upload-btn"
                onClick={(e) => handleActionClick(e, doc, "upload")}
              >
                <BiUpload />
                {doc.status === "Requested"
                  ? "Upload Documents"
                  : "Upload More"}
              </button>
            )}
            {role !== "customer" && doc.status === "Uploaded" && (
              <button
                className="view-btn"
                onClick={(e) => handleActionClick(e, doc, "view")}
              >
                <FaFileAlt />
                View Documents
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // ==================== MAIN RENDER ====================

  return (
    <div className="tab-content">
      <div className="additional-docs-container">
        {/* Header Section */}
        <div className="docs-header">
          <div className="docs-summary">
            <p>
              <strong>Total Additional Documents - </strong> {additonaldocs.length}
            </p>
          </div>

          <div className="docs-actions-right">
            {/* View Toggles - Only show if there is data */}
            {additonaldocs.length > 0 && (
              <div className="view-toggles">
                <button
                  className={`toggle-btn ${
                    viewMode === "grid" ? "active" : ""
                  }`}
                  onClick={() => setViewMode("grid")}
                  title="Grid View"
                >
                  <IoGrid />
                </button>
                <button
                  className={`toggle-btn ${
                    viewMode === "table" ? "active" : ""
                  }`}
                  onClick={() => setViewMode("table")}
                  title="Table View"
                >
                  <FaTableList />
                </button>
              </div>
            )}

            {/* Add Document Button - Only for non-customers */}
            {role !== "customer" && (
              <div className="add-doc-btn">
                <button onClick={() => setShowModal(true)}>
                  Add Request For Doc
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        {additonaldocs.length === 0 ? (
          <div className="no-data-container">
            <h3 className="no-data-title">No Requested Documents</h3>
            <p className="no-data-message">
              No documents have been requested for this case yet.
            </p>
          </div>
        ) : viewMode === "table" ? (
          renderTableView()
        ) : (
          renderGridView()
        )}
      </div>

      {/* Add Document Request Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <DocRequestForm
              initialData={{ addedCourtRef }}
              selectedCourtType={selectedCourtType}
              onSuccess={async (courtRef) => {
                setShowModal(false);
                if (typeof refreshDocs === "function") {
                  await refreshDocs();
                } else {
                  console.warn("refreshDocs is not a function");
                }
                showToast(
                  "Document request submitted successfully!",
                  "success"
                );
              }}
              onCancel={() => setShowModal(false)}
              isModal={true}
            />
          </div>
        </div>
      )}

      {/* View/Upload Document Modal */}
      <ViewDocumentModal
        isOpen={isViewDocModalOpen}
        onClose={() => {
          setIsViewDocModalOpen(false);
          setUploadModalDoc(null);
          setUploadModalMode("view");
        }}
        courtRef={courtRef}
        mode={uploadModalMode}
        documentData={uploadModalDoc}
        onSuccess={async () => {
          if (typeof refreshDocs === "function") {
            await refreshDocs();
          }
        }}
      />
    </div>
  );
};
